import type React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import { TipTapErrorBoundary } from './TipTapErrorBoundary';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import Image from '@tiptap/extension-image';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { UrlInputDialog } from './ui/url-input-dialog';
import {
  AlertCircle,
  Bold,
  Italic,
  Strikethrough,
  Quote,
  Minus,
  Type,
  List,
  ListOrdered,
  CheckSquare,
  Heading2,
  Heading3,
  LinkIcon,
  Unlink,
  Table as TableIcon,
  Plus,
  Trash2,
  ImageIcon,
  Sparkles,
} from 'lucide-react';
import { useDebouncedSave, type SaveStatus } from '../hooks/useDebouncedSave';
import { markdownToHtml } from '../utils/markdown';
import { cn } from '../lib/utils';
import { RegenerateExtension } from '../extensions/RegenerateExtension';

export interface ArticleSection {
  index: number;
  title: string;
  content: string;
  editedContent?: string;
  wordCount: number;
  metadata?: {
    sectionDescription?: string;
    targetWordCount?: number;
    lastEdited?: string;
    isEdited?: boolean;
  };
}

interface ArticleSectionEditorProps {
  articleId: string;
  section: ArticleSection;
  sectionIndex: number;
  onContentChange?: (content: string) => void;
  canRegenerate?: boolean;
  onGenerate?: (sectionIndex: number) => void;
  isGenerating?: boolean;
  needsReview?: boolean;
  streamingContent?: string; // New prop for streaming content
  isStreaming?: boolean; // New prop to indicate streaming state
}

interface ToolbarButtonProps {
  onClick: () => void;
  isActive: boolean;
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  onClick,
  isActive,
  icon,
  label,
  disabled = false,
}) => (
  <Button
    type="button"
    variant={isActive ? 'default' : 'ghost'}
    size="sm"
    onClick={onClick}
    disabled={disabled}
    className={cn('h-8 w-8 p-0', isActive && 'bg-primary text-primary-foreground')}
    title={label}
  >
    {icon}
  </Button>
);

// Toolbar button group components
const TextFormattingButtons: React.FC<{
  editor: Editor;
  isEditingDisabled: boolean;
}> = ({ editor, isEditingDisabled }) => (
  <>
    <ToolbarButton
      onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      isActive={editor.isActive('heading', { level: 1 })}
      icon={<Type className="h-4 w-4" />}
      label="Heading 1 (Ctrl+Alt+1)"
      disabled={isEditingDisabled}
    />
    <ToolbarButton
      onClick={() => editor.chain().focus().toggleBold().run()}
      isActive={editor.isActive('bold')}
      icon={<Bold className="h-4 w-4" />}
      label="Bold (Ctrl+B)"
      disabled={isEditingDisabled}
    />
    <ToolbarButton
      onClick={() => editor.chain().focus().toggleItalic().run()}
      isActive={editor.isActive('italic')}
      icon={<Italic className="h-4 w-4" />}
      label="Italic (Ctrl+I)"
      disabled={isEditingDisabled}
    />
    <ToolbarButton
      onClick={() => editor.chain().focus().toggleStrike().run()}
      isActive={editor.isActive('strike')}
      icon={<Strikethrough className="h-4 w-4" />}
      label="Strikethrough (Ctrl+Shift+S)"
      disabled={isEditingDisabled}
    />
  </>
);

const BlockElementButtons: React.FC<{
  editor: Editor;
  isEditingDisabled: boolean;
}> = ({ editor, isEditingDisabled }) => (
  <>
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={() => editor.chain().focus().setHorizontalRule().run()}
      disabled={isEditingDisabled}
      className="h-8 w-8 p-0"
      title="Horizontal Rule"
    >
      <Minus className="h-4 w-4" />
    </Button>
    <ToolbarButton
      onClick={() => editor.chain().focus().toggleBlockquote().run()}
      isActive={editor.isActive('blockquote')}
      icon={<Quote className="h-4 w-4" />}
      label="Blockquote (Ctrl+Shift+>)"
      disabled={isEditingDisabled}
    />
  </>
);

const HeadingButtons: React.FC<{
  editor: Editor;
  isEditingDisabled: boolean;
}> = ({ editor, isEditingDisabled }) => (
  <>
    <ToolbarButton
      onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      isActive={editor.isActive('heading', { level: 2 })}
      icon={<Heading2 className="h-4 w-4" />}
      label="Heading 2 (Ctrl+Alt+2)"
      disabled={isEditingDisabled}
    />
    <ToolbarButton
      onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      isActive={editor.isActive('heading', { level: 3 })}
      icon={<Heading3 className="h-4 w-4" />}
      label="Heading 3 (Ctrl+Alt+3)"
      disabled={isEditingDisabled}
    />
  </>
);

const ListButtons: React.FC<{
  editor: Editor;
  isEditingDisabled: boolean;
}> = ({ editor, isEditingDisabled }) => (
  <>
    <ToolbarButton
      onClick={() => editor.chain().focus().toggleBulletList().run()}
      isActive={editor.isActive('bulletList')}
      icon={<List className="h-4 w-4" />}
      label="Bullet List"
      disabled={isEditingDisabled}
    />
    <ToolbarButton
      onClick={() => editor.chain().focus().toggleOrderedList().run()}
      isActive={editor.isActive('orderedList')}
      icon={<ListOrdered className="h-4 w-4" />}
      label="Numbered List"
      disabled={isEditingDisabled}
    />
    <ToolbarButton
      onClick={() => editor.chain().focus().toggleTaskList().run()}
      isActive={editor.isActive('taskList')}
      icon={<CheckSquare className="h-4 w-4" />}
      label="Task List"
      disabled={isEditingDisabled}
    />
  </>
);

const LinkButtons: React.FC<{
  editor: Editor;
  isEditingDisabled: boolean;
  onOpenUrlDialog: (type: 'link' | 'image') => void;
}> = ({ editor, isEditingDisabled, onOpenUrlDialog }) => (
  <>
    <ToolbarButton
      onClick={() => onOpenUrlDialog('link')}
      isActive={editor.isActive('link')}
      icon={<LinkIcon className="h-4 w-4" />}
      label="Add Link (Ctrl+K)"
      disabled={isEditingDisabled}
    />
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={() => editor.chain().focus().unsetLink().run()}
      disabled={!editor.isActive('link') || isEditingDisabled}
      className="h-8 w-8 p-0"
      title="Remove Link"
    >
      <Unlink className="h-4 w-4" />
    </Button>
  </>
);

const TableButtons: React.FC<{
  editor: Editor;
  isEditingDisabled: boolean;
}> = ({ editor, isEditingDisabled }) => (
  <>
    <Button
      type="button"
      variant={editor.isActive('table') ? 'default' : 'ghost'}
      size="sm"
      onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
      disabled={editor.isActive('table') || isEditingDisabled}
      className="h-8 w-8 p-0"
      title="Insert Table"
    >
      <TableIcon className="h-4 w-4" />
    </Button>
    {editor.isActive('table') && (
      <>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().addColumnBefore().run()}
          disabled={isEditingDisabled}
          className="h-8 w-8 p-0"
          title="Add Column Before"
        >
          <Plus className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().addRowBefore().run()}
          disabled={isEditingDisabled}
          className="h-8 w-8 p-0"
          title="Add Row Before"
        >
          <Plus className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().deleteColumn().run()}
          disabled={isEditingDisabled}
          className="h-8 w-8 p-0"
          title="Delete Column"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().deleteRow().run()}
          disabled={isEditingDisabled}
          className="h-8 w-8 p-0"
          title="Delete Row"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().deleteTable().run()}
          disabled={isEditingDisabled}
          className="h-8 px-2"
          title="Delete Table"
        >
          <span className="text-xs">Delete Table</span>
        </Button>
      </>
    )}
  </>
);

const ImageButton: React.FC<{
  isEditingDisabled: boolean;
  onOpenUrlDialog: (type: 'link' | 'image') => void;
}> = ({ isEditingDisabled, onOpenUrlDialog }) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    onClick={() => onOpenUrlDialog('image')}
    disabled={isEditingDisabled}
    className="h-8 w-8 p-0"
    title="Insert Image"
  >
    <ImageIcon className="h-4 w-4" />
  </Button>
);

const Toolbar: React.FC<{
  editor: Editor | null;
  hasContent: boolean;
  isGenerating?: boolean;
  isStreaming?: boolean;
  onOpenUrlDialog: (type: 'link' | 'image') => void;
}> = ({
  editor,
  hasContent,
  isGenerating = false,
  isStreaming = false,
  onOpenUrlDialog,
}) => {
  if (!editor) return null;

  // Disable editing buttons when section has no content, is regenerating, or is streaming
  const isEditingDisabled = !hasContent || isGenerating || isStreaming;

  return (
    <div className="border-b border-ochre/10 flex items-center justify-between gap-1 p-2 flex-wrap">
      {/* Left side: formatting buttons */}
      <div className="flex items-center gap-1 flex-wrap">
        <TextFormattingButtons editor={editor} isEditingDisabled={isEditingDisabled} />
        
        <div className="w-px h-6 bg-border mx-1" />
        <BlockElementButtons editor={editor} isEditingDisabled={isEditingDisabled} />
        
        <div className="w-px h-6 bg-border mx-1" />
        <HeadingButtons editor={editor} isEditingDisabled={isEditingDisabled} />
        
        <div className="w-px h-6 bg-border mx-1" />
        <ListButtons editor={editor} isEditingDisabled={isEditingDisabled} />
        
        <div className="w-px h-6 bg-border mx-1" />
        <LinkButtons editor={editor} isEditingDisabled={isEditingDisabled} onOpenUrlDialog={onOpenUrlDialog} />
        
        <div className="w-px h-6 bg-border mx-1" />
        <TableButtons editor={editor} isEditingDisabled={isEditingDisabled} />
        
        <div className="w-px h-6 bg-border mx-1" />
        <ImageButton isEditingDisabled={isEditingDisabled} onOpenUrlDialog={onOpenUrlDialog} />
      </div>

    </div>
  );
};

const SaveStatusIndicator: React.FC<{
  status: SaveStatus;
  lastSaved: Date | null;
}> = ({ status }) => {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      {status === 'error' && (
        <>
          <AlertCircle className="h-3 w-3 text-destructive" />
          <span className="text-destructive">Failed to save</span>
        </>
      )}
    </div>
  );
};

export const ArticleSectionEditor: React.FC<ArticleSectionEditorProps> = ({
  articleId,
  section,
  sectionIndex,
  onContentChange,
  canRegenerate = true,
  onGenerate,
  isGenerating = false,
  needsReview = false,
  streamingContent,
  isStreaming = false,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [urlDialogType, setUrlDialogType] = useState<'link' | 'image'>('link');


  const { debouncedSave, saveStatus, lastSaved, cancelPendingSave } =
    useDebouncedSave({
      articleId,
      sectionIndex,
      delay: 1000,
      onSaveComplete: () => {
        setError(null);
      },
      onSaveError: (errorMessage) => {
        setError(errorMessage);
      },
    });

  // Use streaming content if available, otherwise use section content
  const displayContent = isStreaming && streamingContent
    ? streamingContent
    : (section.editedContent || section.content);
  
  // Check if content is effectively empty (not just whitespace or empty HTML tags)
  const hasContent = useMemo(() => {
    if (!displayContent || displayContent.trim().length === 0) {
      return false;
    }
    
    // Remove HTML tags and check if there's actual text content
    const textContent = displayContent.replace(/<[^>]*>/g, '').trim();
    return textContent.length > 0;
  }, [displayContent]);

  // Convert markdown to HTML for TipTap with error handling (memoized for performance)
  const htmlContent = useMemo(() => {
    try {
      return markdownToHtml(displayContent);
    } catch (error) {
      console.error('Error converting markdown to HTML:', error);
      setError('Failed to convert content: ' + (error as Error).message);
      return '<p>Error loading content</p>';
    }
  }, [displayContent]);

  // Initialize editor with error handling
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4],
        },
        bulletList: {
          HTMLAttributes: {
            class: 'list-disc ml-4',
          },
        },
        orderedList: {
          HTMLAttributes: {
            class: 'list-decimal ml-4',
          },
        },
        link: false,
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
        HTMLAttributes: {
          class: 'task-list-item',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline hover:text-blue-800 cursor-pointer',
        },
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'border-collapse table-auto w-full my-4',
        },
      }),
      TableRow,
      TableHeader.configure({
        HTMLAttributes: {
          class: 'border border-border bg-secondary px-4 py-2 font-bold text-left',
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'border border-border px-4 py-2',
        },
      }),
      Image.configure({
        inline: true,
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg my-4',
        },
      }),
      Placeholder.configure({
        placeholder: 'Start editing this section...',
      }),
      RegenerateExtension,
    ],
    content: htmlContent,
    editable: !isStreaming,
    editorProps: {
      attributes: {
        class:
          'tiptap-editor prose prose-slate max-w-none focus:outline-none min-h-[200px] [&>*:first-child]:mt-0 font-serif',
      },
    },
    onUpdate: ({ editor }: { editor: Editor }) => {
      try {
        const html = editor.getHTML();
        const textContent = html.replace(/<[^>]*>/g, '').trim();
        
        // Always notify parent of content changes, even when empty
        if (onContentChange) {
          onContentChange(textContent.length === 0 ? '' : html);
        }
        
        // Only save non-empty content to prevent 400 errors
        if (textContent.length > 0) {
          const currentSectionContent = displayContent;
          if (html !== currentSectionContent) {
            debouncedSave(html);
          }
        }
      } catch (error) {
        console.error('Error in editor onUpdate:', error);
        setError('Failed to save changes: ' + (error as Error).message);
      }
    },
    onCreate: ({ editor }: { editor: Editor }) => {
      try {
        console.log('TipTap editor initialized successfully:', {
          sectionIndex,
          isEditable: editor.isEditable,
          hasContent: editor.getHTML().length > 0,
          extensionCount: Object.keys(editor.extensionManager.extensions).length,
          extensions: Object.keys(editor.extensionManager.extensions),
        });
      } catch (error) {
        console.error('TipTap editor onCreate error:', error);
        setError('Failed to initialize editor: ' + (error as Error).message);
      }
    },
  });

  // Update editor editability when streaming state changes
  useEffect(() => {
    if (editor) {
      const shouldBeEditable = !isStreaming && hasContent;
      console.log('Setting editable state', { shouldBeEditable, isStreaming, hasContent, sectionIndex });
      editor.setEditable(shouldBeEditable);
    }
  }, [editor, isStreaming, hasContent, sectionIndex]);



  // Update editor content when section content changes externally (e.g., after regeneration)
  useEffect(() => {
    if (editor && displayContent) {
      const newHtmlContent = markdownToHtml(displayContent);
      const currentContent = editor.getHTML();
      
      // Only update if content actually changed to avoid unnecessary re-renders
      if (newHtmlContent !== currentContent) {
        // If streaming, set content and move cursor to end for typing effect
        if (isStreaming && streamingContent) {
          editor.commands.setContent(newHtmlContent);
          // Move cursor to end for streaming effect with proper cleanup
          const timeoutId = setTimeout(() => {
            editor.commands.focus('end');
            
            // Re-enforce non-editable state after focus
            if (isStreaming) {
              editor.setEditable(false);
            }
          }, 0);
          
          // Cleanup timeout on unmount or when dependencies change
          return () => clearTimeout(timeoutId);
        } else {
          editor.commands.setContent(newHtmlContent);
        }
      }
    }
  }, [displayContent, editor, isStreaming, streamingContent]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelPendingSave();
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor, cancelPendingSave]);

  // Handle URL dialog submission
  const handleUrlSubmit = (url: string) => {
    if (!editor) return;
    
    if (urlDialogType === 'link') {
      editor.chain().focus().setLink({ href: url }).run();
    } else {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  return (
    <TipTapErrorBoundary>
      <div className="overflow-hidden relative">
        {/* URL Input Dialog */}
        <UrlInputDialog
          open={urlDialogOpen}
          onOpenChange={setUrlDialogOpen}
          onSubmit={handleUrlSubmit}
          title={urlDialogType === 'link' ? 'Insert Link' : 'Insert Image'}
          description={
            urlDialogType === 'link'
              ? 'Enter the URL for the link'
              : 'Enter the URL for the image'
          }
        />


      {/* Header */}
      <div className="bg-paper-warm/30 border-b border-ochre/10 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">
                Section {sectionIndex + 1} - {section.title}
              </p>
              <p className="text-xs text-muted-foreground">
                {section.wordCount.toLocaleString()} words
                {section.metadata?.targetWordCount && (
                  <span className="ml-2">
                    (target: {section.metadata.targetWordCount.toLocaleString()})
                  </span>
                )}
              </p>
            </div>
            {section.metadata?.isEdited && (
              <Badge variant="secondary" className="ml-2">
                Edited
              </Badge>
            )}
            {needsReview && (
              <Badge variant="outline" className="ml-2 text-chart-4">
                Needs Review
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-3">
            <SaveStatusIndicator status={saveStatus} lastSaved={lastSaved} />
          </div>
        </div>

        {error && (
          <div className="mt-2 flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* Toolbar */}
      <Toolbar
        editor={editor}
        hasContent={hasContent}
        isGenerating={isGenerating}
        isStreaming={isStreaming}
        onOpenUrlDialog={(type) => {
          setUrlDialogType(type);
          setUrlDialogOpen(true);
        }}
      />

      {/* Editor */}
      <div className="relative min-h-96 px-4 pb-4">
        {isGenerating && !isStreaming ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/6" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : !hasContent && onGenerate ? (
          <div className="relative min-h-[24rem]">
            {/* Background skeleton */}
            <div className="space-y-4 opacity-30">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
              <div className="pt-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-4/5" />
                <Skeleton className="h-4 w-full" />
              </div>
              <div className="pt-4">
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </div>
            
            {/* Centered generate button on top */}
            <div className="absolute inset-0 flex items-center justify-center">
              <Button
                type="button"
                variant="default"
                size="lg"
                onClick={() => onGenerate(sectionIndex)}
                disabled={!canRegenerate || isGenerating}
                className="gap-2 shadow-lg"
              >
                <Sparkles className="h-4 w-4" />
                Generate Section
              </Button>
            </div>
          </div>
        ) : (
          <EditorContent editor={editor} />
        )}
      </div>


      </div>
    </TipTapErrorBoundary>
  );
};
